import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://www.ishivax.com";
const DEFAULT_IMAGE = `${SITE_URL}/images/og-image.png`;

const PAGE_SEO = {
    "/": {
        title: "iShivax | AI, Software, IoT & Mobile App Development Company",
        description: "iShivax delivers AI, custom software, web applications, mobile apps, SaaS platforms, IoT solutions and cloud services worldwide.",
        keywords: "custom software development, mobile app development, IoT solutions, SaaS development, web application development"
    },
    "/home": {
        title: "iShivax | AI, Software, IoT & Mobile App Development Company",
        description: "iShivax delivers AI, custom software, web applications, mobile apps, SaaS platforms, IoT solutions and cloud services worldwide.",
        keywords: "custom software development, mobile app development, IoT solutions, SaaS development, web application development",
        canonicalPath: "/"
    },
    "/aboutus": {
        title: "About iShivax | Leading Software Development Company",
        description: "Learn about iShivax, a technology team building custom software, mobile apps, web platforms, IoT automation and cloud solutions for businesses worldwide.",
        keywords: "about iShivax, software development company, app development team, IoT development company"
    },
    "/services": {
        title: "IT Services | AI, IoT, Web, Mobile & Software Solutions",
        description: "Explore iShivax services for custom software, web applications, mobile apps, IoT, automation, SaaS platforms, cloud solutions and digital marketing.",
        keywords: "custom software development, mobile app development, web application development, IoT solutions, SaaS development"
    },
    "/portfolio": {
        title: "Our Portfolio | Software, AI, IoT & Mobile App Projects",
        description: "Explore iShivax portfolio projects across custom software, web applications, mobile apps, eCommerce, IoT and digital platforms.",
        keywords: "iShivax portfolio, software portfolio, app development portfolio, web development projects"
    },
    "/contact-us": {
        title: "Contact iShivax | Get a Free Software Development Consultation",
        description: "Contact iShivax for custom software development, mobile apps, web platforms, IoT solutions, digital marketing and technology consulting.",
        keywords: "contact iShivax, software development inquiry, app development quote, IT services contact"
    },
    "/service-mobile-app": {
        title: "Mobile App Development Services | iShivax",
        description: "Build custom mobile apps for iOS, Android and cross-platform experiences with iShivax's mobile app development team.",
        keywords: "mobile app development, Android app development, iOS app development, cross-platform apps"
    },
    "/jointeam": {
        title: "Careers at iShivax | Join Our Software Development Team",
        description: "Explore career opportunities and join the iShivax team building software, mobile apps, web platforms, IoT and digital products.",
        keywords: "iShivax careers, software developer jobs, app developer jobs, join iShivax"
    },
    "/join-our-team": {
        title: "Careers at iShivax | Join Our Software Development Team",
        description: "Apply to join iShivax and work on software development, mobile apps, web applications, IoT, cloud and digital product projects.",
        keywords: "join our team, iShivax jobs, technology careers, software development careers"
    },
    "/blog": {
        title: "Technology Blog | AI, Software, IoT & Digital Insights",
        description: "Read iShivax blogs on custom software, mobile app development, web platforms, IoT, cloud, digital marketing and business technology.",
        keywords: "software development blog, app development blog, IoT blog, technology insights, iShivax blog"
    },
    "/privacy-policy": {
        title: "Privacy Policy | iShivax Software Development Company",
        description: "Read the iShivax privacy policy to understand how we collect, use and protect information shared through our website and services.",
        keywords: "iShivax privacy policy, privacy, data protection"
    },
    "/terms-conditions": {
        title: "Terms & Conditions | iShivax",
        description: "Read the terms and conditions for using the iShivax website, services, content and digital resources.",
        keywords: "iShivax terms, terms and conditions, website terms"
    },
    "/product-portfolio": {
        title: "Our Portfolio | Software, AI, IoT & Mobile App Projects",
        description: "View iShivax product portfolio examples including web platforms, mobile applications, SaaS tools and digital product builds.",
        keywords: "product portfolio, digital products, SaaS products, mobile app portfolio"
    },
    "/our-partner": {
        title: "Our Partners | iShivax Technology and Business Network",
        description: "Discover iShivax partners and technology relationships supporting software development, digital platforms and business growth.",
        keywords: "iShivax partners, technology partners, business partners"
    },
    "/our-products": {
        title: "Software Products | SaaS, CRM, ERP & Business Solutions",
        description: "Explore iShivax products and platforms built for modern businesses, including web, mobile, SaaS and digital solutions.",
        keywords: "iShivax products, software products, SaaS platforms, business software"
    },
    "/quote": {
        title: "Request a Free Quote | Custom Software Development | iShivax",
        description: "Request a project quote from iShivax for custom software, mobile apps, web applications, IoT solutions and digital platforms.",
        keywords: "software development quote, app development quote, project estimate, iShivax quote"
    },
    "/digital-marketing": {
        title: "Digital Marketing Services | iShivax",
        description: "Grow your online presence with iShivax digital marketing services, SEO, campaigns, content and performance-focused strategies.",
        keywords: "digital marketing services, SEO services, online marketing, performance marketing"
    },
    "/iot-solution": {
        title: "IoT Solutions and Automation Services | iShivax",
        description: "Build connected IoT systems, automation platforms and smart device solutions with iShivax's IoT development expertise.",
        keywords: "IoT solutions, automation services, smart devices, IoT development"
    },
    "/view-cto-details": {
        title: "CTO Details | iShivax Technology Leadership",
        description: "Learn more about iShivax technology leadership, technical expertise and the team guiding software and digital product delivery.",
        keywords: "iShivax CTO, technology leadership, software expertise"
    },
    "/fun-zone": {
        title: "Fun Zone | iShivax",
        description: "Visit the iShivax Fun Zone for interactive digital experiences, experiments and creative technology showcases.",
        keywords: "iShivax fun zone, digital experience, interactive technology"
    },
    "/tech-news": {
        title: "Tech News | iShivax",
        description: "Follow technology news and updates from iShivax covering software, apps, AI, IoT, cloud and digital innovation.",
        keywords: "tech news, software news, app development news, technology updates"
    },
    "/faqs": {
        title: "FAQs | iShivax Software Development Services",
        description: "Find answers to frequently asked questions about iShivax software development, app development, IoT, project process and services.",
        keywords: "iShivax FAQ, software development questions, app development FAQ"
    },
    "/jaipur-software-office": {
        title: "Our Development Headquarters in Jaipur, Rajasthan",
        description: "Visit iShivax development headquarters in Jaipur, Rajasthan. Our software development, mobile app, IoT, web application, and IT services are delivered from Banipark, Jaipur.",
        keywords: "software company in Jaipur, Jaipur software office, app development company Jaipur, IoT solutions Jaipur"
    }
};

const getSeoForPath = (pathname) => {
    if (PAGE_SEO[pathname]) return PAGE_SEO[pathname];

    if (pathname.startsWith("/blog/")) {
        return {
            title: "iShivax Blog Detail | Software, App and Technology Insights",
            description: "Read this iShivax blog article for insights about software development, mobile apps, web platforms, IoT and digital technology.",
            keywords: "iShivax blog, software development article, technology insights"
        };
    }

    if (pathname.startsWith("/services-details/")) {
        return {
            title: "Service Detail | iShivax",
            description: "Learn more about this iShivax service for custom software, mobile apps, web platforms, IoT, cloud and digital solutions.",
            keywords: "iShivax service, software development service, app development service"
        };
    }

    return {
        title: "iShivax | Custom Software, App, Web and IoT Development",
        description: "iShivax builds custom software, mobile apps, web applications, SaaS platforms, IoT solutions and digital products for businesses worldwide.",
        keywords: "iShivax, custom software development, mobile app development, web development, IoT solutions"
    };
};

const getCanonicalUrl = (pathname, canonicalPath) => {
    const normalizedPath = canonicalPath ?? pathname;

    if (normalizedPath === "/") return `${SITE_URL}/`;

    return `${SITE_URL}${normalizedPath}`;
};

const GlobalSeo = () => {
    const { pathname } = useLocation();
    const seo = getSeoForPath(pathname);
    const canonicalUrl = getCanonicalUrl(pathname, seo.canonicalPath);

    return (
        <Helmet>
            <title>{seo.title}</title>
            <meta name="description" content={seo.description} />
            <meta name="keywords" content={seo.keywords} />
            <link rel="canonical" href={canonicalUrl} />
            <meta property="og:title" content={seo.title} />
            <meta property="og:description" content={seo.description} />
            <meta property="og:url" content={canonicalUrl} />
            <meta property="og:type" content="website" />
            <meta property="og:image" content={seo.image || DEFAULT_IMAGE} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={seo.title} />
            <meta name="twitter:description" content={seo.description} />
            <meta name="twitter:image" content={seo.image || DEFAULT_IMAGE} />
        </Helmet>
    );
};

export default GlobalSeo;
